import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import api from '@/lib/api';
import {
  Scissors,
  AlertTriangle,
  TrendingDown,
  TrendingUp,
  DollarSign,
  Lightbulb,
  BarChart3,
  ArrowRight,
  ArrowDown,
  ArrowUp,
  RefreshCw,
  Users,
  Building,
  Zap,
  Megaphone,
  Wrench,
  Loader2,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const CATEGORIA_ICONS = {
  custo_pessoal: Users,
  despesas_administrativas: Building,
  energia_agua: Zap,
  marketing: Megaphone,
  manutencao: Wrench,
  outros: DollarSign
};

const CATEGORIA_COLORS = {
  custo_pessoal: '#ef4444',
  despesas_administrativas: '#f59e0b',
  energia_agua: '#3b82f6',
  marketing: '#ec4899',
  manutencao: '#8E52AB',
  outros: '#6b7280'
};

export default function CustosDashboard() {
  const [loading, setLoading] = useState(true);
  const [mes, setMes] = useState(new Date().toISOString().slice(0, 7));
  const [dashboard, setDashboard] = useState(null);
  const [sugestoes, setSugestoes] = useState(null);
  const [comparativo, setComparativo] = useState(null);
  const [historico, setHistorico] = useState(null);
  const [categoriaExpandida, setCategoriaExpandida] = useState(null);
  const [analiseCategoria, setAnaliseCategoria] = useState(null);

  useEffect(() => {
    loadData();
  }, [mes]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [dashRes, sugRes, compRes, histRes] = await Promise.all([
        api.get(`/custos/dashboard?mes=${mes}`),
        api.get('/custos/sugestoes'),
        api.get('/custos/comparativo'),
        api.get('/custos/historico')
      ]);
      setDashboard(dashRes.data);
      setSugestoes(sugRes.data);
      setComparativo(compRes.data);
      setHistorico(histRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleExpandCategoria = async (categoria) => {
    if (categoriaExpandida === categoria) {
      setCategoriaExpandida(null);
      setAnaliseCategoria(null);
      return;
    }
    
    try {
      const res = await api.get(`/custos/analise-detalhada/${categoria}`);
      setAnaliseCategoria(res.data);
      setCategoriaExpandida(categoria);
    } catch (error) {
      toast.error('Erro ao carregar análise');
    }
  };

  const getUrgenciaColor = (urgencia) => {
    switch (urgencia) {
      case 'crítico': return 'bg-red-500 text-white';
      case 'alerta': return 'bg-orange-500 text-white';
      case 'atenção': return 'bg-yellow-500';
      default: return 'bg-gray-200';
    }
  };

  const getTendenciaIcon = (tendencia) => {
    if (tendencia === 'aumento') return <ArrowUp className="w-4 h-4 text-red-500" />;
    if (tendencia === 'redução') return <ArrowDown className="w-4 h-4 text-green-500" />;
    return <ArrowRight className="w-4 h-4 text-gray-500" />;
  };

  // Dados para gráfico de pizza
  const pieData = dashboard ? Object.entries(dashboard.categorias)
    .filter(([_, value]) => value > 0)
    .map(([key, value]) => ({
      name: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      value,
      color: CATEGORIA_COLORS[key] || '#6b7280'
    })) : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
            <Scissors className="w-8 h-8 text-red-600" />
            Painel Inteligente de Custos
          </h1>
          <p className="text-muted-foreground mt-1">
            Análise e sugestões de redução de despesas
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={mes} onValueChange={setMes}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Array.from({ length: 12 }, (_, i) => {
                const d = new Date();
                d.setMonth(d.getMonth() - i);
                const v = d.toISOString().slice(0, 7);
                return <SelectItem key={v} value={v}>{v}</SelectItem>;
              })}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={loadData}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Alertas Críticos */}
      {dashboard?.alertas_criticos > 0 && (
        <Card className="border-red-300 bg-red-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-8 h-8 text-red-600" />
              <div>
                <p className="font-bold text-red-800">
                  {dashboard.alertas_criticos} Alerta{dashboard.alertas_criticos > 1 ? 's' : ''} Crítico{dashboard.alertas_criticos > 1 ? 's' : ''}
                </p>
                <p className="text-sm text-red-600">
                  Custos acima do limite recomendado. Ação imediata necessária.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Resumo do Mês */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-xs text-muted-foreground">Receita Faturada</p>
            <p className="text-lg font-bold text-blue-600">
              {(dashboard?.resumo.receita_faturada || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-xs text-muted-foreground">Receita Recebida</p>
            <p className="text-lg font-bold text-green-600">
              {(dashboard?.resumo.receita_recebida || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-xs text-muted-foreground">Total Despesas</p>
            <p className="text-lg font-bold text-red-600">
              {(dashboard?.resumo.total_despesas || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-xs text-muted-foreground">Resultado</p>
            <p className={`text-lg font-bold ${(dashboard?.resumo.resultado_operacional || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {(dashboard?.resumo.resultado_operacional || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-xs text-muted-foreground">Margem</p>
            <p className={`text-lg font-bold ${(dashboard?.resumo.margem_operacional || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {(dashboard?.resumo.margem_operacional || 0).toFixed(1)}%
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="alertas" className="space-y-4">
        <TabsList className="grid grid-cols-4 gap-1">
          <TabsTrigger value="alertas" className="flex items-center gap-1">
            <AlertTriangle className="w-4 h-4" />
            <span className="hidden md:inline">Alertas</span>
          </TabsTrigger>
          <TabsTrigger value="categorias" className="flex items-center gap-1">
            <BarChart3 className="w-4 h-4" />
            <span className="hidden md:inline">Categorias</span>
          </TabsTrigger>
          <TabsTrigger value="sugestoes" className="flex items-center gap-1">
            <Lightbulb className="w-4 h-4" />
            <span className="hidden md:inline">Sugestões</span>
          </TabsTrigger>
          <TabsTrigger value="evolucao" className="flex items-center gap-1">
            <TrendingDown className="w-4 h-4" />
            <span className="hidden md:inline">Evolução</span>
          </TabsTrigger>
        </TabsList>

        {/* Tab Alertas */}
        <TabsContent value="alertas" className="space-y-4">
          {dashboard?.alertas.length === 0 ? (
            <Card className="border-green-200 bg-green-50">
              <CardContent className="p-8 text-center">
                <TrendingDown className="w-12 h-12 mx-auto text-green-600 mb-4" />
                <p className="text-lg font-medium text-green-800">Parabéns! Nenhum alerta de custos</p>
                <p className="text-sm text-green-600">Todas as categorias estão dentro dos limites</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {dashboard?.alertas.map((alerta, idx) => {
                const Icon = CATEGORIA_ICONS[alerta.categoria] || DollarSign;
                return (
                  <Card key={idx} className="border-l-4" style={{ borderLeftColor: CATEGORIA_COLORS[alerta.categoria] }}>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Icon className="w-5 h-5" style={{ color: CATEGORIA_COLORS[alerta.categoria] }} />
                          {alerta.nome}
                        </CardTitle>
                        <Badge className={getUrgenciaColor(alerta.urgencia)}>
                          {alerta.urgencia.toUpperCase()}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground">Valor Atual</p>
                          <p className="font-bold">
                            {alerta.valor_atual.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">% da Receita</p>
                          <p className="font-bold text-red-600">{alerta.percentual_atual.toFixed(1)}%</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Limite</p>
                          <p className="font-bold">{alerta.limite_percentual}%</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Excedente</p>
                          <p className="font-bold text-red-600">
                            {alerta.excedente.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                          </p>
                        </div>
                      </div>
                      
                      <div>
                        <p className="text-sm font-medium mb-2">Sugestões de Redução:</p>
                        <div className="space-y-1">
                          {alerta.sugestoes.map((sug, i) => (
                            <div key={i} className="flex items-start gap-2 text-sm">
                              <Lightbulb className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                              <span>{sug}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleExpandCategoria(alerta.categoria)}
                      >
                        {categoriaExpandida === alerta.categoria ? (
                          <>
                            <ChevronUp className="w-4 h-4 mr-1" />
                            Recolher Análise
                          </>
                        ) : (
                          <>
                            <ChevronDown className="w-4 h-4 mr-1" />
                            Ver Análise Detalhada
                          </>
                        )}
                      </Button>

                      {/* Análise Expandida */}
                      {categoriaExpandida === alerta.categoria && analiseCategoria && (
                        <div className="pt-4 border-t space-y-4">
                          <div className="grid md:grid-cols-2 gap-4">
                            {/* Evolução */}
                            <Card>
                              <CardHeader className="py-2">
                                <CardTitle className="text-sm">Evolução dos Últimos 6 Meses</CardTitle>
                              </CardHeader>
                              <CardContent>
                                <ResponsiveContainer width="100%" height={200}>
                                  <LineChart data={analiseCategoria.evolucao}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="mes" tick={{ fontSize: 10 }} />
                                    <YAxis tickFormatter={(v) => `${v.toFixed(0)}%`} />
                                    <Tooltip formatter={(v) => `${v.toFixed(1)}%`} />
                                    <Line 
                                      type="monotone" 
                                      dataKey="percentual" 
                                      stroke={CATEGORIA_COLORS[alerta.categoria]}
                                      strokeWidth={2}
                                    />
                                  </LineChart>
                                </ResponsiveContainer>
                              </CardContent>
                            </Card>

                            {/* Top Despesas */}
                            <Card>
                              <CardHeader className="py-2">
                                <CardTitle className="text-sm">Maiores Despesas do Mês</CardTitle>
                              </CardHeader>
                              <CardContent>
                                <div className="space-y-2">
                                  {analiseCategoria.top_despesas.slice(0, 5).map((d, i) => (
                                    <div key={i} className="flex justify-between text-sm">
                                      <span className="truncate max-w-[200px]">{d.descricao || 'Sem descrição'}</span>
                                      <span className="font-medium">
                                        {d.valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </CardContent>
                            </Card>
                          </div>

                          <div className="flex items-center gap-4 text-sm">
                            <span>Média 6 meses: <strong>{analiseCategoria.media_percentual.toFixed(1)}%</strong></span>
                            <span className="flex items-center gap-1">
                              Tendência: 
                              {analiseCategoria.tendencia === 'alta' ? (
                                <span className="flex items-center text-red-600">
                                  <TrendingUp className="w-4 h-4" /> Alta
                                </span>
                              ) : (
                                <span className="flex items-center text-green-600">
                                  <TrendingDown className="w-4 h-4" /> Baixa
                                </span>
                              )}
                            </span>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Tab Categorias */}
        <TabsContent value="categorias">
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Composição das Despesas</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={100}
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Detalhamento por Categoria</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {Object.entries(dashboard?.categorias || {}).map(([key, value]) => {
                    const Icon = CATEGORIA_ICONS[key] || DollarSign;
                    const percentual = dashboard?.resumo.receita_faturada > 0 
                      ? (value / dashboard.resumo.receita_faturada * 100) 
                      : 0;
                    return (
                      <div key={key} className="space-y-1">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Icon className="w-4 h-4" style={{ color: CATEGORIA_COLORS[key] }} />
                            <span className="text-sm capitalize">{key.replace(/_/g, ' ')}</span>
                          </div>
                          <span className="text-sm font-medium">
                            {value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                          </span>
                        </div>
                        <Progress 
                          value={percentual} 
                          className="h-2"
                          style={{ '--progress-color': CATEGORIA_COLORS[key] }}
                        />
                        <p className="text-xs text-muted-foreground text-right">{percentual.toFixed(1)}% da receita</p>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Tab Sugestões */}
        <TabsContent value="sugestoes">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-yellow-500" />
                Sugestões de Economia
              </CardTitle>
              <CardDescription>
                Economia potencial mensal: {' '}
                <span className="font-bold text-green-600">
                  {(sugestoes?.economia_potencial_mensal || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </span>
                {' '} | Anual: {' '}
                <span className="font-bold text-green-600">
                  {(sugestoes?.economia_potencial_anual || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </span>
              </CardDescription>
            </CardHeader>
            <CardContent>
              {sugestoes?.sugestoes.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Lightbulb className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>Nenhuma sugestão de economia no momento</p>
                  <p className="text-sm">Seus custos estão dentro dos limites!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {sugestoes?.sugestoes.map((sug, idx) => (
                    <div 
                      key={idx} 
                      className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg"
                    >
                      <Badge className={getUrgenciaColor(sug.urgencia)} variant="outline">
                        {sug.urgencia}
                      </Badge>
                      <div className="flex-1">
                        <p className="text-sm font-medium">{sug.sugestao}</p>
                        <p className="text-xs text-muted-foreground">{sug.categoria}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-green-600">
                          {sug.economia_potencial.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                        </p>
                        <p className="text-xs text-muted-foreground">economia/mês</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Evolução */}
        <TabsContent value="evolucao" className="space-y-6">
          {/* Comparativo Período */}
          {comparativo && (
            <Card>
              <CardHeader>
                <CardTitle>Comparativo com Mês Anterior</CardTitle>
                <CardDescription>
                  {comparativo.periodo_anterior.mes} vs {comparativo.periodo_atual.mes}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4 mb-6">
                  <div className="flex items-center gap-2">
                    <span>Variação Receita:</span>
                    {getTendenciaIcon(comparativo.variacao_receita >= 0 ? 'aumento' : 'redução')}
                    <span className={comparativo.variacao_receita >= 0 ? 'text-green-600' : 'text-red-600'}>
                      {comparativo.variacao_receita >= 0 ? '+' : ''}{comparativo.variacao_receita.toFixed(1)}%
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span>Variação Despesas:</span>
                    {getTendenciaIcon(comparativo.variacao_despesas >= 0 ? 'aumento' : 'redução')}
                    <span className={comparativo.variacao_despesas <= 0 ? 'text-green-600' : 'text-red-600'}>
                      {comparativo.variacao_despesas >= 0 ? '+' : ''}{comparativo.variacao_despesas.toFixed(1)}%
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  {comparativo.comparativo.slice(0, 5).map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-muted/50 rounded">
                      <span className="text-sm">{item.nome}</span>
                      <div className="flex items-center gap-2">
                        {getTendenciaIcon(item.tendencia)}
                        <span className={`text-sm font-medium ${
                          item.tendencia === 'redução' ? 'text-green-600' : 
                          item.tendencia === 'aumento' ? 'text-red-600' : ''
                        }`}>
                          {item.variacao_percentual >= 0 ? '+' : ''}{item.variacao_percentual.toFixed(1)}%
                        </span>
                        <span className="text-xs text-muted-foreground">
                          ({item.variacao_absoluta >= 0 ? '+' : ''}{item.variacao_absoluta.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })})
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Histórico Anual */}
          {historico && (
            <Card>
              <CardHeader>
                <CardTitle>Histórico Anual {historico.ano}</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={historico.meses}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="mes" tick={{ fontSize: 10 }} />
                    <YAxis tickFormatter={(v) => `R$ ${(v/1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })} />
                    <Legend />
                    <Bar dataKey="receita" name="Receita" fill="#22c55e" />
                    <Bar dataKey="total_despesas" name="Despesas" fill="#ef4444" />
                  </BarChart>
                </ResponsiveContainer>

                <div className="grid grid-cols-3 gap-4 mt-4 text-center">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Receita</p>
                    <p className="font-bold text-green-600">
                      {historico.totais.receita.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Despesas</p>
                    <p className="font-bold text-red-600">
                      {historico.totais.despesas.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Resultado</p>
                    <p className={`font-bold ${historico.totais.resultado >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {historico.totais.resultado.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
